import api from './client';

export interface SystemSetting {
  id: string;
  key: string;
  value?: string;
  description?: string;
  type: 'string' | 'number' | 'boolean' | 'json';
  category?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSystemSettingDto {
  key: string;
  value?: string;
  description?: string;
  type: 'string' | 'number' | 'boolean' | 'json';
  category?: string;
}

export interface UpdateSystemSettingDto {
  key?: string;
  value?: string;
  description?: string;
  type?: 'string' | 'number' | 'boolean' | 'json';
  category?: string;
}

export interface UpdateSystemSettingsDto {
  'facility.name'?: string;
  'facility.address'?: string;
  'facility.city'?: string;
  'facility.state'?: string;
  'facility.postalCode'?: string;
  'facility.country'?: string;
  'facility.phone'?: string;
  'facility.email'?: string;
  'facility.website'?: string;
  'facility.facilityId'?: string;
  'currency.default'?: string;
  'currency.symbol'?: string;
  'system.timezone'?: string;
  'system.dateFormat'?: string;
  'system.language'?: string;
}

export interface FacilityInfo {
  name: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  phone: string;
  email: string;
  website: string;
  facilityId: string;
}

export interface CurrencyInfo {
  default: string;
  symbol: string;
}

export interface SystemInfo {
  timezone: string;
  dateFormat: string;
  language: string;
}

export const systemSettingsApi = {
  getAll: () => api.get<SystemSetting[]>('system-settings'),
  getByCategory: (category: string) => api.get<SystemSetting[]>(`system-settings/category/${category}`),
  getByKey: (key: string) => api.get<SystemSetting>(`system-settings/${key}`),
  getFacilityInfo: () => api.get<FacilityInfo>('system-settings/facility'),
  getCurrencyInfo: () => api.get<CurrencyInfo>('system-settings/currency'),
  getSystemInfo: () => api.get<SystemInfo>('system-settings/system'),
  getFacilityId: () => api.get<{ facilityId: string; isValid: boolean }>('system-settings/facility/id'),
  regenerateFacilityId: () => api.post<{ facilityId: string; message: string }>('system-settings/facility/id/regenerate'),
  validateFacilityId: () => api.get<{ facilityId: string; isValid: boolean; message: string }>('system-settings/facility/id/validate'),
  create: (data: CreateSystemSettingDto) => api.post<SystemSetting>('system-settings', data),
  update: (key: string, data: UpdateSystemSettingDto) => api.put<SystemSetting>(`system-settings/${key}`, data),
  updateBulk: (data: UpdateSystemSettingsDto) => api.put<SystemSetting[]>('system-settings/bulk', data),
  delete: (key: string) => api.delete(`system-settings/${key}`),
};

// Common timezone options
export const TIMEZONE_OPTIONS = [
  { value: 'Africa/Johannesburg', label: 'South Africa (SAST)' },
  { value: 'Europe/London', label: 'United Kingdom (GMT/BST)' },
  { value: 'America/New_York', label: 'Eastern Time (EST/EDT)' },
  { value: 'America/Chicago', label: 'Central Time (CST/CDT)' },
  { value: 'America/Denver', label: 'Mountain Time (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PST/PDT)' },
  { value: 'Europe/Paris', label: 'Central European Time (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (AEST/AEDT)' },
];

// Common currency options
export const CURRENCY_OPTIONS = [
  { value: 'ZAR', label: 'South African Rand (ZAR)', symbol: 'R' },
  { value: 'USD', label: 'US Dollar (USD)', symbol: '$' },
  { value: 'EUR', label: 'Euro (EUR)', symbol: '€' },
  { value: 'GBP', label: 'British Pound (GBP)', symbol: '£' },
  { value: 'AUD', label: 'Australian Dollar (AUD)', symbol: 'A$' },
  { value: 'CAD', label: 'Canadian Dollar (CAD)', symbol: 'C$' },
  { value: 'JPY', label: 'Japanese Yen (JPY)', symbol: '¥' },
];

// Date format options
export const DATE_FORMAT_OPTIONS = [
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2023)' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2023)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2023-12-31)' },
  { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY (31-12-2023)' },
  { value: 'MM-DD-YYYY', label: 'MM-DD-YYYY (12-31-2023)' },
];

// Language options
export const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'af', label: 'Afrikaans' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'pt', label: 'Portuguese' },
]; 